// v2 — DDoS Exceptions (layerd_ddos_exceptions table read by Layerd's
// AD-engine). cidrs/hostnames/countries/cities/asns/expires_at.
import React, { useMemo } from "react";
import { validateDdosExceptionForm } from "../../../../../utils/validation";
import { useDrawerBaselineDirty, useGuardedDrawerClose } from "../../super/design";
import {
  Stack, Box, Typography, Grid, Button, IconButton, Chip,
  Table, TableBody, TableCell, TableHead, TableRow,
  Modal, Slide, Select, InputLabel, MenuItem, Autocomplete,
  OutlinedInput, Menu, LinearProgress, Tooltip,
} from "@mui/material";
import { Helmet } from "react-helmet-async";
import ReactPerfectScrollbar from "react-perfect-scrollbar";
import userAxios from "../../../../../utils/axios/v1/userAxios";
import useAuth from "../../../../../hooks/useAuth";
import useSite from "../../../../../hooks/user/useSite";
import useGlobalSite from "../../../../../hooks/user/useGlobalSite";
import { useNotification } from "../../../../../contexts/NotificationContext";
import { FireWallExpressionCondition } from "../../../../../utils/constants";
import { GreenSwitch, SafeTextField, GreenSelectForm, GreenDateTimeLocalField } from "../../../../common/Greens";
import colors from "../../../../../utils/colors";
import V2PageHeader from "../../../../common/V2PageHeader";
import SiteScopeGate from "../../../../common/SiteScopeGate";
import { RefreshButton, PrimaryButton, SaveButton, CancelButton, containedButtonSx } from "../../../../common/Button";
import FormHelpTooltip from "../../../../common/FormHelpTooltip";
import DeleteConfirmModal from "../../../../common/DeleteConfirmModal";
import LayerdIcon from "../../../../common/LayerdIcon";
import { UI_ICONS } from "../../../../../icons/uiLucideIcons";
import countries from "../../../application/rule/countries";
import { ReactComponent as CloseIcon } from "../../../../../vendor/_new/icon/navbar/notification/close_big.svg";
import { ReactComponent as InfoIcon } from "../../../../../vendor/_new/icon/sites/info.svg";
import { ReactComponent as DdosIcon } from "../../../../../vendor/_new/icon/sidebar/services/ddos.svg";
import { ReactComponent as ArrowdownIcon } from "../../../../../vendor/_new/icon/sites/arrow_down.svg";
import { ReactComponent as DuplicateIcon } from "../../../../../vendor/_new/icon/services/duplicate.svg";
import { ReactComponent as DeleteRowIcon } from "../../../../../vendor/_new/icon/services/delete.svg";
import { ReactComponent as PlusIcon } from "../../../../../vendor/_new/icon/sites/plus.svg";
import { tableHeadSx, tableBodySx } from "../../detection/WAFEvent";
import { generateMenuSx } from "../../../../../layouts/_new/Normal";
import StickySaveBar from "../../../../common/StickySaveBar";
import { cloneItems, collectRuleEnabledDiff } from "../../../../common/stickySaveBarUtils";

const toolbarPrimaryButtonSx = {
  height: 40,
  minHeight: 40,
  minWidth: 180,
  px: 2,
  py: 0,
  fontSize: 14,
  lineHeight: "21px",
  fontWeight: 400,
  whiteSpace: "nowrap",
  "& .MuiButton-startIcon": { mr: 1 },
};

const helperTextSx = { fontSize: 12, color: colors.neutral[600], mx: 0, mt: 0.5 };

const menuItemSx = {
  height: 38,
  fontSize: 14,
  lineHeight: "21px",
  "&:hover": { color: colors.primary[300] },
};

const DDOS_FIELD_NONE = "none";
const DDOS_OPERATOR_NONE = "none";

const DDOS_MATCH_FIELDS = [
  { key: "cidrs", label: "IPv4 address", helper: "IPv4 address only, e.g. 1.23.45.67" },
  { key: "hostnames", label: "Hostname glob", helper: "Host patterns — * one label, ** zero or more, e.g. *.test.example.com" },
  { key: "countries", label: "Country", helper: "ISO country code" },
  { key: "cities", label: "City", helper: "City name, e.g. London" },
  { key: "asns", label: "ASN", helper: "Autonomous System Numbers, e.g. 64512", numeric: true },
];

const EMPTY_MATCH_ROW = () => ({
  field: DDOS_FIELD_NONE,
  operator: DDOS_OPERATOR_NONE,
  value: "",
});

function exceptionToMatchRows(exception) {
  const rows = [];
  if (!exception) return [EMPTY_MATCH_ROW()];

  for (const { key } of DDOS_MATCH_FIELDS) {
    if (key === "countries") {
      for (const code of exception.countries || []) {
        rows.push({
          field: "countries",
          operator: FireWallExpressionCondition.EQUALS,
          value: String(code).toUpperCase(),
        });
      }
      continue;
    }
    if (key === "asns") {
      for (const asn of exception.asns || []) {
        rows.push({
          field: "asns",
          operator: FireWallExpressionCondition.EQUALS,
          value: String(asn),
        });
      }
      continue;
    }
    for (const v of exception[key] || []) {
      if (!v) continue;
      rows.push({
        field: key,
        operator: FireWallExpressionCondition.EQUALS,
        value: String(v),
      });
    }
  }

  return rows.length ? rows : [EMPTY_MATCH_ROW()];
}

function matchRowsToCriteria(rows) {
  const result = { cidrs: [], hostnames: [], countries: [], cities: [], asns: [] };

  for (const row of rows) {
    if (!row.field || row.field === DDOS_FIELD_NONE) continue;

    if (row.field === "countries") {
      const code = (row.value || "").trim().toUpperCase();
      if (code) result.countries.push(code);
      continue;
    }

    if (row.field === "asns") {
      const n = parseInt(row.value, 10);
      if (Number.isFinite(n)) result.asns.push(n);
      continue;
    }

    const v = (row.value || "").trim();
    if (v) result[row.field].push(v);
  }

  result.cidrs = Array.from(new Set(result.cidrs));
  result.hostnames = Array.from(new Set(result.hostnames));
  result.countries = Array.from(new Set(result.countries));
  result.cities = Array.from(new Set(result.cities));
  result.asns = Array.from(new Set(result.asns));

  return result;
}

function DdosCountryValue({ value, onChange, disabled }) {
  const selected = countries.find((c) => c.value === String(value || "").toUpperCase()) || null;
  return (
    <Autocomplete
      fullWidth
      options={countries}
      autoHighlight
      disabled={disabled}
      popupIcon={<ArrowdownIcon />}
      getOptionLabel={(o) => `${o.label} (${o.value})`}
      isOptionEqualToValue={(o, v) => o.value === v.value}
      value={selected}
      onChange={(_, option) => onChange(option?.value || "")}
      renderOption={(props, option) => (
        <Box
          component="li"
          {...props}
          sx={{ fontSize: 14, lineHeight: "21px", color: colors.neutral[1000] }}
        >
          {option.label} ({option.value})
        </Box>
      )}
      renderInput={(params) => (
        <SafeTextField
          {...params}
          label="Value"
          placeholder="Select a country"
          sx={{ fontSize: 14, lineHeight: "21px", color: colors.neutral[1000] }}
        />
      )}
    />
  );
}

function MatchConditionsField({ rows, setRows, rowErrors = [], touched = {}, onTouchField }) {
  const updateRow = (index, patch) => {
    setRows(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const rowTouched = (index, field) => touched[`${index}.${field}`];
  const showErr = (index, field) => rowTouched(index, field) && rowErrors[index]?.[field];

  const addRow = () => setRows([...rows, EMPTY_MATCH_ROW()]);

  const removeRow = (index) => {
    if (rows.length <= 1) {
      setRows([EMPTY_MATCH_ROW()]);
      return;
    }
    setRows(rows.filter((_, i) => i !== index));
  };

  const fieldMeta = (key) => DDOS_MATCH_FIELDS.find((f) => f.key === key);

  return (
    <Box sx={{ width: "100%", p: 4, borderRadius: "8px", border: `solid 1px ${colors.neutral[200]}` }}>
      {rows.map((row, index) => {
        const meta = fieldMeta(row.field);
        const fieldSelected = row.field && row.field !== DDOS_FIELD_NONE;
        const isCountry = row.field === "countries";
        const valueDisabled = !fieldSelected || row.operator === DDOS_OPERATOR_NONE;

        return (
          <Box key={index} sx={{ mb: index < rows.length - 1 ? 3 : 0 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography sx={{ fontSize: 16, lineHeight: "24px", color: colors.neutral[1000] }}>
                  {index === 0 ? "When incoming requests match…" : "And"}
                </Typography>
                {index === 0 && (
                  <Typography sx={{ fontSize: 12, lineHeight: "18px", color: colors.neutral[600], mt: 1 }}>
                    AND across rows. Each row adds one match criterion. At least one is required.
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <GreenSelectForm error={Boolean(showErr(index, "field"))}>
                  <InputLabel id={`ddos-field-${index}`}>Field</InputLabel>
                  <Select
                    labelId={`ddos-field-${index}`}
                    fullWidth
                    IconComponent={ArrowdownIcon}
                    value={row.field}
                    onBlur={() => onTouchField?.(index, "field")}
                    onChange={(e) => {
                      const nextField = e.target.value;
                      updateRow(index, {
                        field: nextField,
                        operator: nextField === DDOS_FIELD_NONE ? DDOS_OPERATOR_NONE : FireWallExpressionCondition.EQUALS,
                        value: "",
                      });
                    }}
                  >
                    {row.field === DDOS_FIELD_NONE && (
                      <MenuItem value={DDOS_FIELD_NONE} disabled sx={{ ...menuItemSx, display: "none!important" }}>
                        Select Field ...
                      </MenuItem>
                    )}
                    {DDOS_MATCH_FIELDS.map((f) => (
                      <MenuItem key={f.key} value={f.key} sx={menuItemSx}>
                        {f.label}
                      </MenuItem>
                    ))}
                  </Select>
                </GreenSelectForm>
              </Grid>
              <Grid item xs={12} sm={6}>
                <GreenSelectForm error={Boolean(showErr(index, "operator"))}>
                  <InputLabel id={`ddos-operator-${index}`}>Operator</InputLabel>
                  <Select
                    labelId={`ddos-operator-${index}`}
                    fullWidth
                    IconComponent={ArrowdownIcon}
                    disabled={!fieldSelected}
                    value={fieldSelected ? row.operator : DDOS_OPERATOR_NONE}
                    onBlur={() => onTouchField?.(index, "operator")}
                    onChange={(e) => updateRow(index, { operator: e.target.value })}
                  >
                    {row.operator === DDOS_OPERATOR_NONE && (
                      <MenuItem value={DDOS_OPERATOR_NONE} disabled sx={{ ...menuItemSx, display: "none!important" }}>
                        Select Item ...
                      </MenuItem>
                    )}
                    <MenuItem value={FireWallExpressionCondition.EQUALS} sx={menuItemSx}>
                      Equals
                    </MenuItem>
                  </Select>
                </GreenSelectForm>
              </Grid>
              <Grid item xs={12}>
                {isCountry ? (
                  <Box onBlur={() => onTouchField?.(index, "value")}>
                    <DdosCountryValue
                      value={row.value}
                      disabled={valueDisabled}
                      onChange={(v) => updateRow(index, { value: v })}
                    />
                    {showErr(index, "value") && (
                      <Typography sx={{ ...helperTextSx, color: "#d32f2f", mt: 0.5 }}>
                        {rowErrors[index].value}
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <SafeTextField
                    fullWidth
                    label="Value"
                    type={meta?.numeric ? "number" : "text"}
                    value={row.value}
                    disabled={valueDisabled}
                    error={Boolean(showErr(index, "value"))}
                    helperText={showErr(index, "value") || meta?.helper}
                    FormHelperTextProps={{ sx: helperTextSx }}
                    onBlur={() => onTouchField?.(index, "value")}
                    onChange={(e) => updateRow(index, { value: e.target.value })}
                  />
                )}
              </Grid>
              <Grid item xs={12}>
                <Stack width="100%" alignItems="center" direction="row" justifyContent="flex-end" spacing={2}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    onClick={addRow}
                    sx={{
                      cursor: "pointer",
                      borderRadius: "8px",
                      border: `solid 1px ${colors.secondary[300]}`,
                      px: 2,
                      py: 1,
                    }}
                  >
                    <DuplicateIcon style={{ color: colors.neutral[800] }} />
                    <Typography sx={{ fontSize: 14, lineHeight: "21px", color: colors.neutral[800] }}>
                      And
                    </Typography>
                  </Stack>
                  <Box onClick={() => removeRow(index)} sx={{ cursor: "pointer", display: "flex" }}>
                    <DeleteRowIcon style={{ color: colors.neutral[600] }} />
                  </Box>
                </Stack>
              </Grid>
              {index === rows.length - 1 && (
                <Grid item xs={12}>
                  <Button
                    startIcon={<PlusIcon style={{ color: colors.neutral[100] }} />}
                    onClick={addRow}
                    sx={{ height: 32, width: 105, ...containedButtonSx }}
                  >
                    Add new
                  </Button>
                </Grid>
              )}
            </Grid>
          </Box>
        );
      })}
    </Box>
  );
}

function ExceptionDrawer({ open, onClose, initial, onSubmit }) {
  const isEdit = !!initial?.id;
  const [name, setName] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [enabled, setEnabled] = React.useState(true);
  const [matchRows, setMatchRows] = React.useState([EMPTY_MATCH_ROW()]);
  const [expiresAt, setExpiresAt] = React.useState("");
  const [touched, setTouched] = React.useState({});
  const [submitAttempted, setSubmitAttempted] = React.useState(false);
  const [baselineKey, setBaselineKey] = React.useState(0);

  const validation = useMemo(
    () => validateDdosExceptionForm({ name, description, matchRows, expiresAt }),
    [name, description, matchRows, expiresAt],
  );

  const touchField = (index, field) => {
    setTouched((prev) => ({ ...prev, [`${index}.${field}`]: true }));
  };

  const touchFormField = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const showFormErr = (field) => (submitAttempted || touched[field]) && validation.formErrors[field];

  React.useEffect(() => {
    if (!open) return;
    setName(initial?.name || "");
    setDescription(initial?.description || "");
    setEnabled(initial ? initial.enabled !== false : true);
    setMatchRows(exceptionToMatchRows(initial));
    setExpiresAt(initial?.expires_at ? new Date(initial.expires_at).toISOString().slice(0, 16) : "");
    setTouched({});
    setSubmitAttempted(false);
    setBaselineKey((k) => k + 1);
  }, [open, initial]);

  const formSnapshot = useMemo(
    () => ({ name, description, enabled, matchRows, expiresAt }),
    [name, description, enabled, matchRows, expiresAt],
  );
  const isDirty = useDrawerBaselineDirty(open, formSnapshot, baselineKey);
  const { requestClose, confirmDialog } = useGuardedDrawerClose(onClose, isDirty);

  const submit = () => {
    setSubmitAttempted(true);
    const result = validateDdosExceptionForm({ name, description, matchRows, expiresAt });
    if (!result.valid) return;

    const criteria = matchRowsToCriteria(matchRows);
    onSubmit({
      name,
      description,
      enabled,
      ...criteria,
      countries: criteria.countries.map((c) => String(c).toUpperCase()),
      expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
    });
  };

  return (
    <>
    <Modal
      open={open}
      onClose={requestClose}
      slotProps={{ backdrop: { sx: { backgroundColor: "rgba(0,0,0,0.5)" } } }}
    >
      <Slide direction="left" in={open} mountOnEnter unmountOnExit>
        <Stack
          sx={{
            position: "fixed",
            top: 0,
            right: 0,
            width: "33%",
            minWidth: 520,
            height: "100vh",
            backgroundColor: "#fff",
            boxShadow: "-4px 0 20px rgba(0,0,0,0.1)",
            zIndex: 1300,
            borderTopLeftRadius: "8px",
          }}
        >
          <Stack direction="row" justifyContent="space-between" spacing={6} sx={{ px: 4, pt: 4, pb: 3 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box sx={{
                width: 48,
                height: 48,
                borderRadius: "8px",
                backgroundColor: "#EEF0FE",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}>
                <DdosIcon style={{ width: 32, height: 32, color: "#6371F7" }} />
              </Box>
              <Stack>
                <Stack direction="row" alignItems="center" sx={{ flexWrap: "nowrap" }}>
                  <Typography sx={{ fontWeight: "medium", fontSize: 20, lineHeight: "30px", color: colors.neutral[1000] }}>
                    {isEdit ? "Edit DDoS exception" : "New DDoS exception"}
                  </Typography>
                  <FormHelpTooltip tooltip="Allowlist traffic that should bypass DDoS mitigation for this site. Matching requests skip challenge and rate-based DDoS actions." />
                </Stack>
                <Typography sx={{ fontSize: 12, lineHeight: "18px", color: colors.neutral[700] }}>
                  Allowlist traffic that should bypass DDoS mitigation for this site.
                </Typography>
              </Stack>
            </Stack>
            <IconButton onClick={requestClose} aria-label="Close" sx={{ p: 0 }}>
              <CloseIcon sx={{ color: colors.neutral[600] }} />
            </IconButton>
          </Stack>

          <Box sx={{ py: 4, pl: 4, height: "calc(100vh - 130px)" }}>
            <ReactPerfectScrollbar>
              <Stack justifyContent="space-between" spacing={4} sx={{ pr: 4, height: "100%" }}>
                <Stack spacing={4} key={initial?.id || "new"}>
                  <SafeTextField
                    fullWidth
                    label="Exception name"
                    value={name}
                    error={Boolean(showFormErr("name"))}
                    helperText={showFormErr("name") || ""}
                    onBlur={() => touchFormField("name")}
                    onChange={(e) => setName(e.target.value)}
                  />

                  <SafeTextField
                    fullWidth
                    multiline
                    minRows={2}
                    label="Description"
                    value={description}
                    error={Boolean(showFormErr("description"))}
                    helperText={showFormErr("description") || ""}
                    onBlur={() => touchFormField("description")}
                    onChange={(e) => setDescription(e.target.value)}
                  />

                  <Grid container spacing={3} alignItems="center">
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: "flex", alignItems: "center", minHeight: 48, gap: 2 }}>
                        <Typography sx={{ fontSize: 14, lineHeight: "21px", color: colors.neutral[800] }}>
                          Enabled
                        </Typography>
                        <GreenSwitch checked={enabled} onChange={() => setEnabled(!enabled)} />
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} sx={{ pl: "1px", pr: "12px" }}>
                      <GreenDateTimeLocalField
                        fullWidth
                        label="Expires at"
                        disablePast
                        value={expiresAt}
                        error={Boolean(showFormErr("expiresAt"))}
                        helperText={showFormErr("expiresAt") || ""}
                        onBlur={() => touchFormField("expiresAt")}
                        onChange={(e) => setExpiresAt(e.target.value)}
                      />
                    </Grid>
                  </Grid>

                  <MatchConditionsField
                    rows={matchRows}
                    setRows={setMatchRows}
                    rowErrors={validation.rowErrors}
                    touched={submitAttempted ? Object.fromEntries(matchRows.flatMap((_, i) =>
                      ["field", "operator", "value"].map((f) => [`${i}.${f}`, true])
                    )) : touched}
                    onTouchField={touchField}
                  />
                  {showFormErr("matchRows") && (
                    <Typography sx={{ fontSize: 12, color: "#d32f2f" }}>
                      {validation.formErrors.matchRows}
                    </Typography>
                  )}

                  <Stack
                    direction="row"
                    spacing={2}
                    sx={{
                      minHeight: 48,
                      p: 2,
                      borderRadius: "8px",
                      backgroundColor: colors.primary.alpha,
                      border: `solid 1px ${colors.primary[300]}`,
                    }}
                  >
                    <Box sx={{ width: 24, height: 24, flexShrink: 0 }}>
                      <InfoIcon style={{ color: colors.primary[300], width: 24, height: 24 }} />
                    </Box>
                    <Typography sx={{ fontSize: 14, lineHeight: "21px", color: colors.primary[300] }}>
                      Matching traffic is excluded from DDoS mitigation. Changes take effect immediately after save.
                    </Typography>
                  </Stack>
                </Stack>

                <Stack direction="row" alignItems="center" spacing={3}>
                  <SaveButton onClick={submit} label={isEdit ? "Save" : "Create"} />
                  <CancelButton onClick={requestClose} />
                </Stack>
              </Stack>
            </ReactPerfectScrollbar>
          </Box>
        </Stack>
      </Slide>
    </Modal>
    {confirmDialog}
    </>
  );
}

const ddosExceptionEnabled = (r) => r.enabled !== false;

const DdosExceptionsPage = () => {
  const { isAuthenticated } = useAuth();
  const { siteList, getSitesForItems } = useSite();
  const { showSuccess, showError } = useNotification();
  const { effectiveSiteUid } = useGlobalSite();
  const siteID = effectiveSiteUid;
  const [rows, setRows] = React.useState(null);
  const [savedRows, setSavedRows] = React.useState(null);
  const [pattern, setPattern] = React.useState("");
  const [isInitialized, setIsInitialized] = React.useState(true);
  const [editing, setEditing] = React.useState(null);
  const [open, setOpen] = React.useState(false);
  const [pendingDelete, setPendingDelete] = React.useState(null);
  const [deleting, setDeleting] = React.useState(false);
  const [actionMenu, setActionMenu] = React.useState({ anchorEl: null, rowId: null });
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const [isSavingToggles, setIsSavingToggles] = React.useState(false);

  React.useEffect(() => {
    if (isAuthenticated) getSitesForItems().then(() => setIsInitialized(false));
  }, [isAuthenticated, getSitesForItems]);
  const reload = React.useCallback(async () => {
    if (!siteID) return;
    try {
      const r = await userAxios.get(`/config/ddos/${siteID}/exceptions`);
      const data = Array.isArray(r.data) ? r.data : [];
      setRows(data);
      setSavedRows(data);
    } catch (e) {
      showError(e?.message || String(e));
      setRows([]);
      setSavedRows([]);
    }
  }, [siteID, showError]);
  React.useEffect(() => { reload(); }, [reload]);

  const handleRefresh = React.useCallback(async () => {
    setIsRefreshing(true);
    try {
      await reload();
    } finally {
      setIsRefreshing(false);
    }
  }, [reload]);

  const submit = async (body) => {
    try {
      if (editing?.id) await userAxios.patch(`/config/ddos/${siteID}/exceptions/${editing.id}`, body);
      else await userAxios.post(`/config/ddos/${siteID}/exceptions`, body);
      showSuccess(editing?.id ? "Saved" : "Created"); setOpen(false); setEditing(null); reload();
    } catch (e) { showError(e?.message || String(e)); }
  };
  const requestDelete = (row) => {
    if (!row) return;
    setPendingDelete(row);
  };

  const executeDelete = async () => {
    const row = pendingDelete;
    if (!row?.id) return;
    setDeleting(true);
    try {
      await userAxios.delete(`/config/ddos/${siteID}/exceptions/${row.id}`);
      showSuccess("DDoS exception deleted");
      setPendingDelete(null);
      reload();
    } catch (e) {
      showError(e?.message || String(e));
    } finally {
      setDeleting(false);
    }
  };

  const stickyModifiedFields = useMemo(
    () => collectRuleEnabledDiff(savedRows, rows, undefined, ddosExceptionEnabled),
    [savedRows, rows],
  );
  const stickyModifiedCount = stickyModifiedFields.length;

  const stageToggleEnabled = (row, next) => {
    if (isSavingToggles || !row?.site_id) return;
    setRows((prev) =>
      (prev || []).map((r) => (r.id === row.id ? { ...r, enabled: next } : r)),
    );
  };

  const discardToggleChanges = React.useCallback(() => {
    setRows(cloneItems(savedRows));
  }, [savedRows]);

  const applyToggleChanges = React.useCallback(async () => {
    if (!siteID || stickyModifiedCount === 0) return;
    const savedById = Object.fromEntries((savedRows || []).map((r) => [r.id, ddosExceptionEnabled(r)]));
    const changed = (rows || []).filter((r) => r.site_id && savedById[r.id] !== ddosExceptionEnabled(r));
    if (changed.length === 0) return;

    setIsSavingToggles(true);
    try {
      await Promise.all(
        changed.map((r) => userAxios.patch(`/config/ddos/${siteID}/exceptions/${r.id}`, {
          enabled: ddosExceptionEnabled(r),
        })),
      );
      setSavedRows(cloneItems(rows));
      showSuccess(`Saved ${changed.length} exception${changed.length === 1 ? "" : "s"}.`);
    } catch (e) {
      showError(e?.message || String(e));
    } finally {
      setIsSavingToggles(false);
    }
  }, [siteID, stickyModifiedCount, savedRows, rows, showError, showSuccess]);

  const filtered = useMemo(() => {
    if (!rows) return [];
    const q = pattern.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const hay = [
        r.name,
        r.description,
        r.exception_id,
        ...(r.cidrs || []),
        ...(r.hostnames || []),
        ...(r.countries || []),
        ...(r.cities || []),
        ...(r.asns || []).map(String),
      ].join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [rows, pattern]);

  return (
    <>
      <Helmet title="DDoS Exceptions" />
      {isInitialized && (
        <Box sx={{ width: "100%", position: "fixed", top: 66, zIndex: 100 }}>
          <LinearProgress sx={{
            "& .MuiLinearProgress-bar1Indeterminate": { backgroundColor: colors.primary[200] },
            "& .MuiLinearProgress-bar2Indeterminate": { backgroundColor: colors.primary[200] },
            height: 4,
          }} />
        </Box>
      )}
      <Stack spacing={6} sx={{ p: 6 }}>
        <V2PageHeader
          title="DDoS Exceptions"
          helpTooltip="Allow-list paths or sources that should not trigger DDoS mitigation. Use sparingly for trusted partners or health-check endpoints."
        />

        <SiteScopeGate mode="single">
          {() => (
            <>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={3}>
          <OutlinedInput
            startAdornment={<LayerdIcon icon={UI_ICONS.search} size={16} color="neutral" />}
            placeholder="Search name, CIDR, hostname…"
            value={pattern}
            onChange={(e) => setPattern(e.target.value)}
            sx={{
              width: 280,
              height: 32,
              borderRadius: "8px",
              fontSize: 14,
              lineHeight: "21px",
              borderColor: colors.neutral[600],
              pl: 2,
              "& .MuiOutlinedInput-input": { px: 2 },
              "& .MuiInputBase-input::placeholder": { fontSize: 14, lineHeight: "21px" },
            }}
          />
          <Stack direction="row" alignItems="center" spacing={2}>
            <RefreshButton onClick={handleRefresh} disabled={isRefreshing} loading={isRefreshing} />
            <PrimaryButton
              startIcon={<LayerdIcon icon={UI_ICONS.plus} size={16} color="inherit" />}
              onClick={() => { setEditing(null); setOpen(true); }}
              sx={toolbarPrimaryButtonSx}
            >
              New DDoS exception
            </PrimaryButton>
          </Stack>
        </Stack>

        <Box sx={{ width: "100%", borderRadius: "8px", border: `1px solid ${colors.neutral[200]}`, p: 2 }}>
          <Table
            sx={{
              "& .MuiTableCell-head": { py: 3, px: 3 },
              "& .MuiTableCell-body": { py: 2, px: 3 },
              "& .MuiTableCell-root": { borderBottom: "none" },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: "6%" }}><Typography sx={tableHeadSx}>No</Typography></TableCell>
                <TableCell sx={{ width: "14%" }}><Typography sx={tableHeadSx}>Exception ID</Typography></TableCell>
                <TableCell sx={{ width: "15%" }}><Typography sx={tableHeadSx}>Name</Typography></TableCell>
                <TableCell sx={{ width: "15%" }}><Typography sx={tableHeadSx}>Description</Typography></TableCell>
                <TableCell sx={{ width: "10%" }}><Typography sx={tableHeadSx}>Scope</Typography></TableCell>
                <TableCell sx={{ width: "20%" }}><Typography sx={tableHeadSx}>Criteria</Typography></TableCell>
                <TableCell sx={{ width: "10%" }}><Typography sx={tableHeadSx}>Expires</Typography></TableCell>
                <TableCell sx={{ width: "8%" }}><Typography sx={tableHeadSx}>Status</Typography></TableCell>
                <TableCell sx={{ width: "2%" }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows === null ? (
                <TableRow>
                  <TableCell colSpan={9}>
                    <Typography sx={tableBodySx} textAlign="center">Loading…</Typography>
                  </TableCell>
                </TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9}>
                    <Typography sx={tableBodySx} textAlign="center">
                      {pattern ? "No exceptions match your search." : "There are no exceptions."}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : filtered.map((r, idx) => {
                const criteria = [
                  r.cidrs?.length && `${r.cidrs.length} CIDR`,
                  r.hostnames?.length && `${r.hostnames.length} host`,
                  r.countries?.length && r.countries.join(","),
                  r.cities?.length && `${r.cities.length} city`,
                  r.asns?.length && `AS${r.asns.join(",")}`,
                ].filter(Boolean);
                return (
                  <TableRow key={r.id}>
                    <TableCell><Typography sx={tableBodySx}>{idx + 1}</Typography></TableCell>
                    <TableCell>
                      <Typography sx={tableBodySx}>
                        {(r.exception_id || "").trim()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={tableBodySx}>
                        {(r.name || "").trim()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={tableBodySx}>{(r.description || "").trim()}</Typography>
                    </TableCell>
                    <TableCell>
                      {r.site_id ? (
                        <Chip label="site" size="small" sx={{
                          fontSize: 11,
                          height: 22,
                          background: colors.neutral[100],
                          color: colors.neutral[700],
                          border: `1px solid ${colors.neutral[200]}`,
                        }} />
                      ) : (
                        <Chip label="global" size="small" sx={{
                          fontSize: 11,
                          height: 22,
                          background: colors.yellow[10],
                          color: colors.neutral[900],
                          border: `1px solid ${colors.yellow[200]}`,
                        }} />
                      )}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {criteria.length === 0 ? (
                          <Typography sx={{ ...tableBodySx, color: colors.neutral[500] }}>—</Typography>
                        ) : criteria.map((c, i) => (
                          <Chip key={i} label={c} size="small" sx={{
                            fontSize: 11,
                            height: 22,
                            mr: 0.5,
                            mb: 0.5,
                            background: colors.neutral[100],
                            color: colors.neutral[800],
                            border: `1px solid ${colors.neutral[200]}`,
                          }} />
                        ))}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography sx={tableBodySx}>
                        {r.expires_at ? new Date(r.expires_at).toLocaleDateString() : "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {!r.site_id ? (
                        <Tooltip
                          title="Global exceptions are managed at the organization level and cannot be changed from this site view."
                          placement="top"
                          arrow
                          componentsProps={{
                            tooltip: {
                              sx: {
                                maxWidth: 360,
                                fontSize: 12,
                                lineHeight: "18px",
                                color: "#fff",
                                backgroundColor: "#1F2937",
                                "& .MuiTooltip-arrow": { color: "#1F2937" },
                                p: 1.5,
                              },
                            },
                          }}
                        >
                          <Box component="span" sx={{ display: "inline-flex" }}>
                            <GreenSwitch
                              checked={ddosExceptionEnabled(r)}
                              disabled
                            />
                          </Box>
                        </Tooltip>
                      ) : (
                        <GreenSwitch
                          checked={ddosExceptionEnabled(r)}
                          disabled={isSavingToggles}
                          onChange={(e) => stageToggleEnabled(r, e.target.checked)}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Stack alignItems="flex-end" sx={{ width: "100%" }}>
                        <Box
                          onClick={(e) => {
                            e.stopPropagation();
                            setActionMenu({ anchorEl: e.currentTarget, rowId: r.id });
                          }}
                          sx={{ cursor: "pointer", width: 24, height: 24 }}
                        >
                          <LayerdIcon icon={UI_ICONS.more} size={18} color="neutral" />
                        </Box>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
            </>
          )}
        </SiteScopeGate>
      </Stack>

      <Menu
        anchorEl={actionMenu.anchorEl}
        open={Boolean(actionMenu.anchorEl)}
        onClose={() => setActionMenu({ anchorEl: null, rowId: null })}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        sx={{
          top: 13,
          left: -12,
          "& .MuiList-root": {
            backgroundColor: "white",
            borderRadius: "8px",
            border: `1px solid ${colors.neutral[200]}`,
            width: 200,
            boxShadow: "none",
            py: 0,
          },
          "& .MuiMenu-paper": { boxShadow: "none" },
        }}
      >
        <Box sx={{ p: 3 }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            onClick={() => {
              const row = rows?.find((item) => item.id === actionMenu.rowId);
              setActionMenu({ anchorEl: null, rowId: null });
              if (row) {
                setEditing(row);
                setOpen(true);
              }
            }}
            sx={generateMenuSx(176, 37)}
          >
            <LayerdIcon icon={UI_ICONS.edit} size={18} color="neutral" />
            <Typography sx={{ fontSize: 14, lineHeight: "21px", pl: 1, color: colors.neutral[800] }}>
              Edit
            </Typography>
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            onClick={() => {
              const row = rows?.find((item) => item.id === actionMenu.rowId);
              setActionMenu({ anchorEl: null, rowId: null });
              if (row) requestDelete(row);
            }}
            sx={generateMenuSx(176, 37)}
          >
            <LayerdIcon icon={UI_ICONS.delete} size={18} color="neutral" />
            <Typography sx={{ fontSize: 14, lineHeight: "21px", pl: 1, color: colors.neutral[800] }}>
              Delete
            </Typography>
          </Stack>
        </Box>
      </Menu>

      <ExceptionDrawer open={open} onClose={() => { setOpen(false); setEditing(null); }} initial={editing} onSubmit={submit} />

      <DeleteConfirmModal
        open={Boolean(pendingDelete)}
        onClose={() => setPendingDelete(null)}
        onConfirm={executeDelete}
        title="Delete DDoS exception"
        loading={deleting}
      >
        Are you sure you want to delete{" "}
        <strong>{pendingDelete?.name}</strong>
        {pendingDelete?.exception_id ? ` (${pendingDelete.exception_id})` : null}? This action cannot be undone.
      </DeleteConfirmModal>
      <StickySaveBar
        open={stickyModifiedCount > 0}
        modifiedCount={stickyModifiedCount}
        modifiedFields={stickyModifiedFields}
        onDiscard={discardToggleChanges}
        onApply={applyToggleChanges}
        isApplying={isSavingToggles}
      />
    </>
  );
};
export default DdosExceptionsPage;
